import { useEffect, useState } from 'react';
import { supabase } from '../../lib/supabase';
import { useAuth } from '../../contexts/AuthContext';
import { useToast } from '../../contexts/ToastContext';
import { Loader2, MessageSquarePlus } from 'lucide-react';

interface Props {
  userId: string;
}

interface Note {
  id: string;
  body: string;
  created_by_display_name: string;
  created_at: string;
}

export function JourneyNotesBlock({ userId }: Props) {
  const auth = useAuth() as any;
  const { showToast } = useToast();
  const [notes, setNotes] = useState<Note[] | null>(null);
  const [draft, setDraft] = useState('');
  const [saving, setSaving] = useState(false);
  const [reload, setReload] = useState(0);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      const { data } = await supabase
        .from('user_journey_notes')
        .select('id, body, created_by_display_name, created_at')
        .eq('user_id', userId)
        .order('created_at', { ascending: false });
      if (!cancelled) setNotes((data ?? []) as Note[]);
    })();
    return () => { cancelled = true; };
  }, [userId, reload]);

  async function add() {
    if (!draft.trim()) return;
    setSaving(true);
    try {
      const { error } = await supabase.from('user_journey_notes').insert({
        user_id: userId,
        body: draft.trim(),
        created_by_auth_user_id: auth.authUser?.id,
        created_by_display_name: auth.user?.first_name || auth.user?.email || 'Support',
      });
      if (error) throw error;
      setDraft('');
      setReload(r => r + 1);
    } catch (e: any) {
      showToast('Notitie opslaan mislukt', 'error', e.message);
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="bg-white rounded-lg border border-slate-200 p-4">
      <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-1.5">
        <MessageSquarePlus className="w-4 h-4" /> Notities
      </h3>
      <div className="flex gap-2 mb-3">
        <textarea
          value={draft}
          onChange={e => setDraft(e.target.value)}
          rows={2}
          placeholder="Korte aantekening over deze klant..."
          className="flex-1 px-3 py-1.5 border border-slate-300 rounded text-sm"
        />
        <button
          onClick={add}
          disabled={saving || !draft.trim()}
          className="px-3 py-1.5 text-sm bg-teal-600 hover:bg-teal-700 text-white rounded self-start disabled:opacity-50"
        >
          {saving ? '...' : 'Toevoegen'}
        </button>
      </div>
      {notes === null ? (
        <Loader2 className="w-4 h-4 animate-spin text-slate-400" />
      ) : notes.length === 0 ? (
        <div className="text-xs text-slate-500 italic">Nog geen notities.</div>
      ) : (
        <ul className="space-y-2">
          {notes.map(n => (
            <li key={n.id} className="text-sm border-l-2 border-slate-200 pl-3 py-1">
              <div className="whitespace-pre-wrap text-slate-800">{n.body}</div>
              <div className="text-xs text-slate-500 mt-0.5">
                {n.created_by_display_name} · {new Date(n.created_at).toLocaleString('nl-NL', { dateStyle: 'short', timeStyle: 'short' })}
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}
